/** Company scope — APP-GLOBAL (OUI-11): selected once, every module recomputes.
 *  Lives in a context so the selector can sit in the sub-tab row of any module. */
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

import { ActionRow } from "@/components/ActionRow";
export interface Company {
  id: string;
  name: string;
  color: string;
  ops: { airtel: number; jio: number; vi: number };
  states: { active: number; idle: number; kyc: number; inventory: number; suspended: number };
  spend: { airtel: number; jio: number; vi: number };
  usageMo: number;
  usageDayKB: number;
  live: { h24: number; h48: number; d7: number; d30: number };
}

export const COMPANIES: Company[] = [
  {
    id: "swift", name: "SwiftFleet", color: "var(--c-swift)",
    ops: { airtel: 700, jio: 300, vi: 0 },
    states: { active: 940, idle: 28, kyc: 12, inventory: 14, suspended: 6 },
    spend: { airtel: 19600, jio: 6300, vi: 0 },
    usageMo: 11.2, usageDayKB: 380,
    live: { h24: 928, h48: 941, d7: 958, d30: 968 },
  },
  {
    id: "green", name: "GreenWheel", color: "var(--c-green)",
    ops: { airtel: 88, jio: 0, vi: 72 },
    states: { active: 128, idle: 3, kyc: 18, inventory: 10, suspended: 1 },
    spend: { airtel: 2464, jio: 0, vi: 1584 },
    usageMo: 8.1, usageDayKB: 270,
    live: { h24: 121, h48: 126, d7: 131, d30: 134 },
  },
  {
    id: "aph", name: "Aphelion Test", color: "var(--c-aph)",
    ops: { airtel: 0, jio: 0, vi: 64 },
    states: { active: 58, idle: 4, kyc: 0, inventory: 0, suspended: 2 },
    spend: { airtel: 0, jio: 0, vi: 1408 },
    usageMo: 15.6, usageDayKB: 520,
    live: { h24: 55, h48: 56, d7: 58, d30: 60 },
  },
  {
    id: "ev", name: "EvRent Co", color: "var(--c-ev)",
    ops: { airtel: 30, jio: 0, vi: 0 },
    states: { active: 27, idle: 2, kyc: 3, inventory: 0, suspended: 0 },
    spend: { airtel: 840, jio: 0, vi: 0 },
    usageMo: 6.2, usageDayKB: 210,
    live: { h24: 25, h48: 26, d7: 27, d30: 27 },
  },
];

interface ScopeCtx {
  ids: string[];
  setIds: (ids: string[]) => void;
  companies: Company[];
  label: string;
}
const Ctx = createContext<ScopeCtx | null>(null);

export function ScopeProvider({ children }: { children: ReactNode }) {
  const [ids, setIds] = useState<string[]>(COMPANIES.map((c) => c.id));
  const value = useMemo<ScopeCtx>(() => {
    const companies = COMPANIES.filter((c) => ids.includes(c.id));
    return {
      ids,
      setIds,
      companies,
      label:
        ids.length === COMPANIES.length
          ? "All companies"
          : ids.length === 1
            ? (companies[0]?.name ?? "—")
            : `${ids.length} companies`,
    };
  }, [ids]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useScope(): ScopeCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useScope outside ScopeProvider");
  return c;
}

export function ScopeSelect() {
  const { ids, setIds, label } = useScope();
  const [open, setOpen] = useState(false);
  const all = ids.length === COMPANIES.length;

  return (
    <div className="scopewrap">
      <button className="scopebtn" onClick={() => setOpen((o) => !o)} aria-haspopup="true">
        🏢 <span>{label}</span>
        <span className="cnt">{ids.length}</span> ▾
      </button>
      {open && (
        <div className="pop open">
          <div className="hd">Company scope - one, many, or all</div>
          <label className="copt">
            <input
              type="checkbox"
              checked={all}
              onChange={(e) => setIds(e.target.checked ? COMPANIES.map((c) => c.id) : [])}
            />
            <b style={{ color: "var(--t-bright)" }}>All companies</b>
          </label>
          {COMPANIES.map((c) => (
            <label className="copt" key={c.id}>
              <input
                type="checkbox"
                checked={ids.includes(c.id)}
                onChange={(e) =>
                  setIds(e.target.checked ? [...ids, c.id] : ids.filter((i) => i !== c.id))
                }
              />
              <span
                style={{ background: c.color, width: 9, height: 9, borderRadius: 3, flexShrink: 0 }}
              />
              {c.name}
              <span className="n num">
                {(c.ops.airtel + c.ops.jio + c.ops.vi).toLocaleString("en-IN")}
              </span>
            </label>
          ))}
          <ActionRow top={10}>
            <button className="btn ghost" onClick={() => setOpen(false)}>
              Close
            </button>
            <button className="btn teal" onClick={() => setOpen(false)}>
              Apply
            </button>
          </ActionRow>
        </div>
      )}
    </div>
  );
}
